/**
 * Badge de estado de equipo — fuente ÚNICA de colores por status, compartida
 * por la tabla de inventario, el board de Office Manager y las métricas
 * (getStatusHex para recharts). Lookup por status NORMALIZADO (trim+lowercase,
 * los datos traen variantes tipo 'Localizar '); los labels muestran el valor
 * crudo — acá nunca se renombra un estado.
 */

import { normalizeDeviceStatus, isDeviceActive, isReadyToDeliver } from '../../../../shared/inventory-status';

interface StatusStyle {
  className: string;
  hex: string;
}

// Tailwind necesita las clases completas en el fuente (no interpola runtime):
const STYLES: Record<string, StatusStyle> = {
  entregado: {
    className: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
    hex: '#22c55e',
  },
  'lista para entregar': {
    className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
    hex: '#3b82f6',
  },
  'pending: revisar': {
    className: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    hex: '#f59e0b',
  },
  'pending: para reparar': {
    className: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    hex: '#d97706',
  },
  'pending: formateo': {
    className: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    hex: '#b45309',
  },
  localizar: {
    className: 'bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800',
    hex: '#06b6d4',
  },
  'en proceso de recupero': {
    className: 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800',
    hex: '#6366f1',
  },
  'vendida a clapper': {
    className: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
    hex: '#a855f7',
  },
  donada: {
    className: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800',
    hex: '#14b8a6',
  },
  vintage: {
    className: 'bg-stone-200 text-stone-700 border-stone-300 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700',
    hex: '#78716c',
  },
  bloqueada: {
    className: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800',
    hex: '#f97316',
  },
  robada: {
    className: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800',
    hex: '#f43f5e',
  },
  'baja total': {
    className: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
    hex: '#ef4444',
  },
};

const FALLBACK: StatusStyle = {
  className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
  hex: '#94a3b8',
};

function styleFor(status?: string | null): StatusStyle {
  return STYLES[normalizeDeviceStatus(status)] ?? FALLBACK;
}

/**
 * Hex del status para charts (recharts). `unassigned` = "Lista para Entregar"
 * sin destinatario: el mismo azul, más claro (Sol 15/09).
 */
export function getStatusHex(status?: string | null, variant?: 'unassigned'): string {
  if (variant === 'unassigned' && isReadyToDeliver(status)) return '#93c5fd';
  return styleFor(status).hex;
}

/** Clases del pill para usos custom (triggers, etc.). */
export function getStatusClasses(status?: string | null): string {
  return styleFor(status).className;
}

export function DeviceStatusBadge({ status, className = '' }: { status: string; className?: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${styleFor(status).className} ${className}`}
    >
      {(status || '').trim() || '—'}
    </span>
  );
}

export { isDeviceActive, normalizeDeviceStatus };
